import { RefreshCw } from 'lucide-react';
import { ReactNode } from 'react';

import { Label } from '@/Domain/components/Shared/Label/Label';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import { Switch } from '@/Domain/components/Shared/Switch/Switch';
import { cn } from '@/Domain/utils/cn';

type Props = {
  id: string;
  checked: boolean;
  disabled?: boolean;
  pending?: boolean;
  label: string;
  description: string;
  descriptionId?: string;
  descriptionTestId?: string;
  switchTestId: string;
  onCheckedChange: (checked: boolean) => void;
  footer?: ReactNode;
};

export const AutoReloadSwitchRow = ({
  id,
  checked,
  disabled,
  pending = false,
  label,
  description,
  descriptionId,
  descriptionTestId,
  switchTestId,
  onCheckedChange,
  footer,
}: Props) => {
  const descriptionElementId = descriptionId ?? `${id}-description`;

  return (
    <div
      className="rounded-xl border border-border bg-card p-4"
      data-testid="credit-topup-auto-reload-row"
      aria-busy={pending}
    >
      <div className="flex items-start gap-3">
        <span
          className={cn(
            'inline-flex size-9 shrink-0 items-center justify-center rounded-lg transition-colors duration-200',
            checked
              ? 'bg-[var(--qc-discord-blurple)]/10 text-[var(--qc-discord-blurple)]'
              : 'bg-muted text-muted-foreground',
          )}
        >
          <RefreshCw className="size-4" aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex min-h-9 items-center justify-between gap-3">
            <Label htmlFor={id} className="cursor-pointer">
              {label}
            </Label>
            <span className="flex items-center gap-2">
              {pending && (
                <Spinner
                  className="size-4 text-muted-foreground"
                  data-testid={`${switchTestId}-pending`}
                />
              )}
              <Switch
                id={id}
                checked={checked}
                disabled={disabled}
                onCheckedChange={onCheckedChange}
                aria-describedby={descriptionElementId}
                data-testid={switchTestId}
              />
            </span>
          </div>
          <p
            id={descriptionElementId}
            className="text-xs leading-relaxed text-muted-foreground"
            aria-live="polite"
            data-testid={descriptionTestId}
          >
            {description}
          </p>
        </div>
      </div>
      {footer}
    </div>
  );
};
